import React from 'react';
import { Users, Edit2, Trash2, CalendarDays } from 'lucide-react';
import { Button } from '../ui/button';
import { buildFilterSummary } from '../../utils/audienceEngine';

/**
 * AudienceCard
 *
 * Displays a single saved audience: name, description, filter summary,
 * estimated contact count, and creation date.
 *
 * Compatible fields for future campaign selection:
 *   audience.id            — reference used by SMS/Email campaign documents
 *   audience.estimatedSize — shown in campaign composer
 *   audience.name          — shown in campaign composer dropdown
 */
const AudienceCard = ({ audience, onEdit, onDelete }) => {
  const filterSummary = buildFilterSummary(audience.filters, audience.filterLogic);

  const createdDate = audience.createdAt?.toDate
    ? audience.createdAt.toDate().toLocaleDateString('en-US', {
        month: 'short',
        day:   'numeric',
        year:  'numeric',
      })
    : '—';

  const filterCount = (audience.filters || []).length;

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 p-5 hover:border-orange-200
        hover:shadow-md transition-all duration-200 group"
      data-testid="audience-card"
    >
      <div className="flex items-start justify-between gap-3">
        {/* Info */}
        <div className="flex-1 min-w-0">
          {/* Icon + Name */}
          <div className="flex items-center gap-2.5 mb-1.5">
            <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
              <Users className="w-4 h-4 text-orange-500" />
            </div>
            <h3 className="font-semibold text-gray-900 truncate">{audience.name}</h3>
          </div>

          {/* Optional description */}
          {audience.description && (
            <p className="text-sm text-gray-500 mb-2 ml-10 line-clamp-2">
              {audience.description}
            </p>
          )}

          {/* Filter summary */}
          <p className="text-xs text-gray-400 mb-2.5 ml-10 truncate" title={filterSummary}>
            {filterSummary}
          </p>

          {/* Meta row */}
          <div className="ml-10 flex items-center gap-4 flex-wrap">
            {/* Contact count */}
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600">
              <Users className="w-3 h-3" />
              ~{(audience.estimatedSize ?? 0).toLocaleString()} contacts
            </span>

            {/* Filter count badge */}
            <span className="inline-flex items-center gap-1 text-xs text-gray-400">
              {filterCount} rule{filterCount !== 1 ? 's' : ''}
              {audience.filterLogic && filterCount > 1 && (
                <span className="ml-0.5 px-1 py-0 rounded bg-gray-100 text-gray-500 font-medium">
                  {audience.filterLogic}
                </span>
              )}
            </span>

            {/* Created date */}
            <span className="inline-flex items-center gap-1 text-xs text-gray-400">
              <CalendarDays className="w-3 h-3" />
              {createdDate}
            </span>
          </div>
        </div>

        {/* Actions — visible on hover */}
        <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(audience)}
            title="Edit audience"
            data-testid="audience-edit-btn"
            className="h-8 w-8 p-0 text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(audience)}
            title="Delete audience"
            data-testid="audience-delete-btn"
            className="h-8 w-8 p-0 text-gray-400 hover:text-red-500 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AudienceCard;
